// src/components/admin/page-management/section-forms/FeaturedProductsForm.js
import React from 'react';
import { Info } from 'lucide-react';
import ColorPicker from '../../shared/ColorPicker';

const FeaturedProductsForm = ({ content, updateContent }) => {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Featured Products Section</h3>
      
      {/* Title Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Title Settings</h4>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showTitle !== false}
              onChange={(e) => updateContent('showTitle', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Title</span>
          </label>
        </div>
        
        {content?.showTitle !== false && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                type="text"
                value={content?.title || 'Featured Products'}
                onChange={(e) => updateContent('title', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Title Color</label>
              <ColorPicker
                color={content?.titleColor || '#111827'}
                onChange={(color) => updateContent('titleColor', color)}
              />
            </div>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.showSeparator !== false}
                  onChange={(e) => updateContent('showSeparator', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Show Separator Lines</span>
              </label>
            </div>
            
            {content?.showSeparator !== false && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Separator Color</label>
                <ColorPicker
                  color={content?.separatorColor || '#9CA3AF'}
                  onChange={(color) => updateContent('separatorColor', color)}
                />
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Search Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Search Settings</h4>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.searchEnabled === true}
              onChange={(e) => updateContent('searchEnabled', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Enable Search</span>
          </label>
        </div>
        
        {content?.searchEnabled === true && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Search Placeholder</label>
            <input
              type="text"
              value={content?.searchPlaceholder || 'Search featured products...'}
              onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>
        )}
      </div>
      
  
      {/* Grid Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Grid Settings</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Maximum Products to Display</label>
          <input
            type="number"
            min="1"
            max="20"
            value={content?.maxProducts || 5}
            onChange={(e) => updateContent('maxProducts', parseInt(e.target.value, 10))}
            className="w-32 p-2 border rounded"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Grid Gap Size</label>
          <input
            type="text"
            value={content?.gapSize || '1.5rem'}
            onChange={(e) => updateContent('gapSize', e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="e.g., 1.5rem, 24px"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Mobile Columns</label>
            <select
              value={(content?.gridColumns?.mobile || 1).toString()}
              onChange={(e) => {
                const currentGridColumns = content?.gridColumns || {};
                updateContent('gridColumns', {
                  ...currentGridColumns,
                  mobile: parseInt(e.target.value, 10)
                });
              }}
              className="w-full p-2 border rounded"
            >
              <option value="1">1 Column</option>
              <option value="2">2 Columns</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tablet Columns</label>
            <select
              value={(content?.gridColumns?.tablet || 2).toString()}
              onChange={(e) => {
                const currentGridColumns = content?.gridColumns || {};
                updateContent('gridColumns', {
                  ...currentGridColumns,
                  tablet: parseInt(e.target.value, 10)
                });
              }}
              className="w-full p-2 border rounded"
            >
              <option value="1">1 Column</option>
              <option value="2">2 Columns</option>
              <option value="3">3 Columns</option>
            </select>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Desktop Columns</label>
            <select
              value={(content?.gridColumns?.desktop || 3).toString()}
              onChange={(e) => {
                const currentGridColumns = content?.gridColumns || {};
                updateContent('gridColumns', {
                  ...currentGridColumns,
                  desktop: parseInt(e.target.value, 10)
                });
              }}
              className="w-full p-2 border rounded"
            >
              <option value="2">2 Columns</option>
              <option value="3">3 Columns</option>
              <option value="4">4 Columns</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Widescreen Columns</label>
            <select
              value={(content?.gridColumns?.widescreen || 4).toString()}
              onChange={(e) => {
                const currentGridColumns = content?.gridColumns || {};
                updateContent('gridColumns', {
                  ...currentGridColumns,
                  widescreen: parseInt(e.target.value, 10)
                });
              }}
              className="w-full p-2 border rounded"
            >
              <option value="3">3 Columns</option>
              <option value="4">4 Columns</option>
              <option value="5">5 Columns</option>
              <option value="6">6 Columns</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Product Card Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Product Card Settings</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Image Aspect Ratio</label>
          <input
            type="text"
            value={content?.imageAspectRatio || '100%'}
            onChange={(e) => updateContent('imageAspectRatio', e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="e.g., 100%, 75%, 4/3"
          />
          <p className="text-xs text-gray-500 mt-1">For example, "100%" creates a square, "75%" creates a 4:3 rectangle</p>
        </div>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showProductDescription === true}
              onChange={(e) => updateContent('showProductDescription', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Product Description</span>
          </label>
        </div>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showPricing !== false}
              onChange={(e) => updateContent('showPricing', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Price</span>
          </label>
        </div>
        
        {content?.showPricing !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Price Color</label>
            <ColorPicker
              color={content?.priceColor || '#2563EB'}
              onChange={(color) => updateContent('priceColor', color)}
            />
          </div>
        )}
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.cardShadowEnabled !== false}
              onChange={(e) => updateContent('cardShadowEnabled', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Card Shadow</span>
          </label>
        </div>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.cardBorderEnabled !== false}
              onChange={(e) => updateContent('cardBorderEnabled', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Card Border</span>
          </label>
        </div>
        
        {content?.cardBorderEnabled !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Card Border Color</label>
            <ColorPicker
              color={content?.cardBorderColor || '#E5E7EB'}
              onChange={(color) => updateContent('cardBorderColor', color)}
            />
          </div>
        )}
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.hoverEffectEnabled !== false}
              onChange={(e) => updateContent('hoverEffectEnabled', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Enable Hover Effect</span>
          </label>
        </div>
      </div>
      
      {/* "View All" Button Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">"View All" Button Settings</h4>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showViewAllButton !== false}
              onChange={(e) => updateContent('showViewAllButton', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show "View All" Button</span>
          </label>
        </div>
        
        {content?.showViewAllButton !== false && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Button Text</label>
              <input
                type="text"
                value={content?.viewAllButtonText || 'View All Products'}
                onChange={(e) => updateContent('viewAllButtonText', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Button Link</label>
              <input
                type="text"
                value={content?.viewAllButtonLink || '/products'}
                onChange={(e) => updateContent('viewAllButtonLink', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Button Color</label>
                <ColorPicker
                  color={content?.viewAllButtonColor || '#033568'}
                  onChange={(color) => updateContent('viewAllButtonColor', color)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button Text Color</label>
                <ColorPicker
                  color={content?.viewAllButtonTextColor || '#ffffff'}
                  onChange={(color) => updateContent('viewAllButtonTextColor', color)}
                />
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Spacing Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Spacing Settings</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Margin Top</label>
            <input
              type="text"
              value={content?.marginTop || '4rem'}
              onChange={(e) => updateContent('marginTop', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 4rem, 64px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Margin Bottom</label>
            <input
              type="text"
              value={content?.marginBottom || '8rem'}
              onChange={(e) => updateContent('marginBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 8rem, 128px"
            />
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Background Color</label>
          <ColorPicker
            color={content?.backgroundColor || '#ffffff'}
            onChange={(color) => updateContent('backgroundColor', color)}
          />
        </div>
      </div>
      
      {/* API Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">API Settings</h4>
          <div className="text-blue-500">
            <Info size={16} />
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          These settings are for advanced users. Most users should leave these at their default values.
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">API Path</label>
          <input
            type="text"
            value={content?.apiPath || '/api/products/basic'}
            onChange={(e) => updateContent('apiPath', e.target.value)}
            className="w-full p-2 border rounded"
          />
          <p className="text-xs text-gray-500 mt-1">
            This endpoint should return products with an "isFeatured" flag.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeaturedProductsForm;
















// // src/components/admin/page-management/section-forms/FeaturedProductsForm.js
// import React from 'react';
// import { Info } from 'lucide-react';
// import ColorPicker from '../../shared/ColorPicker';

// const FeaturedProductsForm = ({ content, updateContent }) => {
//   return (
//     <div className="space-y-6">
//       <h3 className="text-lg font-semibold">Featured Products Section</h3>
      
//       {/* Title Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Title Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showTitle !== false}
//               onChange={(e) => updateContent('showTitle', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Title</span>
//           </label>
//         </div>
        
//         {content?.showTitle !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Title</label>
//               <input
//                 type="text"
//                 value={content?.title || 'Featured Products'}
//                 onChange={(e) => updateContent('title', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Title Color</label>
//               <ColorPicker
//                 color={content?.titleColor || '#111827'}
//                 onChange={(color) => updateContent('titleColor', color)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.showSeparator !== false}
//                   onChange={(e) => updateContent('showSeparator', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Show Separator Lines</span>
//               </label>
//             </div>
            
//             {content?.showSeparator !== false && (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Separator Color</label>
//                 <ColorPicker
//                   color={content?.separatorColor || '#9CA3AF'}
//                   onChange={(color) => updateContent('separatorColor', color)}
//                 />
//               </div>
//             )}
//           </>
//         )}
//       </div>
      
//       {/* Search Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Search Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.searchEnabled === true}
//               onChange={(e) => updateContent('searchEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Search</span>
//           </label>
//         </div>
        
//         {content?.searchEnabled === true && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Search Placeholder</label>
//             <input
//               type="text"
//               value={content?.searchPlaceholder || 'Search featured products...'}
//               onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
//               className="w-full p-2 border rounded"
//             />
//           </div>
//         )}
//       </div>
      
//       {/* Grid Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Grid Settings</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Maximum Products to Display</label>
//           <input
//             type="number"
//             min="1"
//             max="20"
//             value={content?.maxProducts || 5}
//             onChange={(e) => updateContent('maxProducts', parseInt(e.target.value, 10))}
//             className="w-32 p-2 border rounded"
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Grid Gap Size</label>
//           <input
//             type="text"
//             value={content?.gapSize || '1.5rem'}
//             onChange={(e) => updateContent('gapSize', e.target.value)}
//             className="w-full p-2 border rounded"
//             placeholder="e.g., 1.5rem, 24px"
//           />
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Mobile Columns</label>
//             <select
//               value={content?.gridColumns?.mobile || 1}
//               onChange={(e) => updateContent('gridColumns', {
//                 ...content?.gridColumns,
//                 mobile: parseInt(e.target.value)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Column</option>
//               <option value={2}>2 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Tablet Columns</label>
//             <select
//               value={content?.gridColumns?.tablet || 2}
//               onChange={(e) => updateContent('gridColumns', {
//                 ...content?.gridColumns,
//                 tablet: parseInt(e.target.value)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Column</option>
//               <option value={2}>2 Columns</option>
//               <option value={3}>3 Columns</option>
//             </select>
//           </div>
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Desktop Columns</label>
//             <select
//               value={content?.gridColumns?.desktop || 3}
//               onChange={(e) => updateContent('gridColumns', {
//                 ...content?.gridColumns,
//                 desktop: parseInt(e.target.value)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={2}>2 Columns</option>
//               <option value={3}>3 Columns</option>
//               <option value={4}>4 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Widescreen Columns</label>
//             <select
//               value={content?.gridColumns?.widescreen || 5}
//               onChange={(e) => updateContent('gridColumns', {
//                 ...content?.gridColumns,
//                 widescreen: parseInt(e.target.value)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={3}>3 Columns</option>
//               <option value={4}>4 Columns</option>
//               <option value={5}>5 Columns</option>
//               <option value={6}>6 Columns</option>
//             </select>
//           </div>
//         </div>
//       </div>
      
//       {/* Product Card Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Product Card Settings</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Image Aspect Ratio</label>
//           <input
//             type="text"
//             value={content?.imageAspectRatio || '100%'}
//             onChange={(e) => updateContent('imageAspectRatio', e.target.value)}
//             className="w-full p-2 border rounded"
//             placeholder="e.g., 100%, 75%, 4/3"
//           />
//           <p className="text-xs text-gray-500 mt-1">For example, "100%" creates a square, "75%" creates a 4:3 rectangle</p>
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showProductDescription === true}
//               onChange={(e) => updateContent('showProductDescription', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Product Description</span>
//           </label>
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showPricing !== false}
//               onChange={(e) => updateContent('showPricing', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Price</span>
//           </label>
//         </div>
        
//         {content?.showPricing !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Price Color</label>
//             <ColorPicker
//               color={content?.priceColor || '#2563EB'}
//               onChange={(color) => updateContent('priceColor', color)}
//             />
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.cardShadowEnabled !== false}
//               onChange={(e) => updateContent('cardShadowEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Card Shadow</span>
//           </label>
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.cardBorderEnabled !== false}
//               onChange={(e) => updateContent('cardBorderEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Card Border</span>
//           </label>
//         </div>
        
//         {content?.cardBorderEnabled !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Card Border Color</label>
//             <ColorPicker
//               color={content?.cardBorderColor || '#E5E7EB'}
//               onChange={(color) => updateContent('cardBorderColor', color)}
//             />
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.hoverEffectEnabled !== false}
//               onChange={(e) => updateContent('hoverEffectEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Hover Effect</span>
//           </label>
//         </div>
//       </div>
      
//       {/* "View All" Button Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">"View All" Button Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showViewAllButton !== false}
//               onChange={(e) => updateContent('showViewAllButton', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show "View All" Button</span>
//           </label>
//         </div>
        
//         {content?.showViewAllButton !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Text</label>
//               <input
//                 type="text"
//                 value={content?.viewAllButtonText || 'View All Products'}
//                 onChange={(e) => updateContent('viewAllButtonText', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Link</label>
//               <input
//                 type="text"
//                 value={content?.viewAllButtonLink || '/products'}
//                 onChange={(e) => updateContent('viewAllButtonLink', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Button Color</label>
//                 <ColorPicker
//                   color={content?.viewAllButtonColor || '#033568'}
//                   onChange={(color) => updateContent('viewAllButtonColor', color)}
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Button Text Color</label>
//                 <ColorPicker
//                   color={content?.viewAllButtonTextColor || '#ffffff'}
//                   onChange={(color) => updateContent('viewAllButtonTextColor', color)}
//                 />
//               </div>
//             </div>
//           </>
//         )}
//       </div>
      
//       {/* Spacing Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Spacing Settings</h4>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Margin Top</label>
//             <input
//               type="text"
//               value={content?.marginTop || '4rem'}
//               onChange={(e) => updateContent('marginTop', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 4rem, 64px"
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Margin Bottom</label>
//             <input
//               type="text"
//               value={content?.marginBottom || '8rem'}
//               onChange={(e) => updateContent('marginBottom', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 8rem, 128px"
//             />
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Background Color</label>
//           <ColorPicker
//             color={content?.backgroundColor || '#ffffff'}
//             onChange={(color) => updateContent('backgroundColor', color)}
//           />
//         </div>
//       </div>
      
//       {/* API Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <div className="flex justify-between items-center mb-4">
//           <h4 className="font-medium">API Settings</h4>
//           <div className="text-blue-500">
//             <Info size={16} />
//           </div>
//         </div>
        
//         <p className="text-sm text-gray-600 mb-4">
//           These settings are for advanced users. Most users should leave these at their default values.
//         </p>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">API Path</label>
//           <input
//             type="text"
//             value={content?.apiPath || '/api/products/basic'}
//             onChange={(e) => updateContent('apiPath', e.target.value)}
//             className="w-full p-2 border rounded"
//           />
//           <p className="text-xs text-gray-500 mt-1">
//             This endpoint should return products with an "isFeatured" flag.
//           </p>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default FeaturedProductsForm;